<template>
  <div class="self-stretch flex flex-col gap-1 pb-2 mb-1 border-b border-border-separator" data-test="brief-finding-card">
    <div class="self-stretch inline-flex justify-between items-center gap-2">
      <div class="font-sans text-sm font-bold text-text-default truncate" :title="domain_label" data-test="domain">
        {{ domain_label }}
      </div>
      <!-- flex-shrink-0 for the same reason the result card's pill has it: the pill states whether this
           data could be read, and it must never be the thing that truncates behind a long domain name. -->
      <div v-if="show_pill" class="flex-shrink-0 px-2 py-0.5 rounded-full font-sans text-[10px] font-bold uppercase tracking-[0.06em]"
           :class="pill_class" data-test="status-pill">
        {{ pill_label }}
      </div>
    </div>
    <div v-if="time_label || store_label" class="self-stretch font-sans text-xs text-text-subtle" data-test="subline">
      <span v-if="time_label" data-test="produced-at">{{ time_label }}</span>
      <span v-if="time_label && store_label" aria-hidden="true"> · </span>
      <span v-if="store_label" data-test="store-scope">{{ store_label }}</span>
    </div>
    <!-- The supporting figures. A definition list rather than a table: there are at most a handful of
         pairs, and a table would impose column alignment across cards that hold unrelated measures. -->
    <dl v-if="metric_rows.length" class="self-stretch mt-1 flex flex-col gap-0.5 mb-0" data-test="metric-rows">
      <div v-for="row in metric_rows" :key="`${row.label}_${row.value}`" class="flex items-baseline justify-between gap-3">
        <!-- On a group brief each row is a store, and its chip names the platform that answered there: stores
             in one group can be answered by different platforms, which the card's Sources cannot attribute. -->
        <!-- The chip follows the count, at the row's end, so the figures stay in one column down the card. -->
        <dt class="font-sans text-xs text-text-subtle truncate">{{ row.label }}</dt>
        <dd class="font-sans text-xs font-bold text-text-default flex-shrink-0 mb-0 flex items-baseline gap-1.5">
          <span>{{ row.value }}</span>
          <span v-if="row.source"
                class="font-normal text-[10px] px-1.5 py-0.5 rounded bg-brand-subtle text-text-link"
                data-test="row-source">{{ row.source }}</span>
        </dd>
      </div>
    </dl>
    <!-- What the figure counts. A card can be answered by different platforms depending on what the owner
         has, and they count slightly different things, so the card says which. Where it came from is not
         repeated here: the message's citations show it as "Sources" beneath, the same as any Rey answer. -->
    <!-- pre-line: a group card answered by several platforms has one definition per platform, a line each. -->
    <div v-if="definition_text" class="self-stretch font-sans text-xs text-text-subtle whitespace-pre-line" data-test="definition">{{ definition_text }}</div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';

/**
 * The identifying band at the top of one morning-brief finding: which domain it belongs to, whether
 * its data could be read, when it was produced, the store, and its supporting figures.
 *
 * Like the scheduled result card it renders the card's HEADER, not the whole card — the finding's
 * headline is the message body and renders beneath this from the ordinary message path. Keeping the
 * sentence out of here is what lets a finding be copied, forked and read by a screen reader as the
 * message it is.
 *
 * Everything it shows is passed in. It fetches nothing and decides nothing, except how to name a
 * store, which it reads out of access info the store already holds.
 */
export default {
  name: 'BriefFindingCard',
  props: {
    /** The fact key the finding was gathered under. The domain label's fallback. */
    findingKey: {
      type: String,
      required: false,
      default: '',
    },
    /** Department the fact belongs to ("Sales", "Service"). Empty for a fact that belongs to everyone. */
    domain: {
      type: String,
      required: false,
      default: '',
    },
    /** BriefFactStatus name: 'Present' or 'Unavailable'. */
    status: {
      type: String,
      required: false,
      default: '',
    },
    /** When the finding was produced, ISO-8601. Rendered in the viewer's zone. */
    producedAt: {
      type: String,
      required: false,
      default: '',
    },
    /** The store the finding belongs to, as a CIF. */
    dealerId: {
      type: String,
      required: false,
      default: '',
    },
    /** Supporting figures, as [{ label, value }]. Already computed server-side from the tool result. */
    metrics: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * The dealer group the brief covers ("All rooftops"), shown where a single store's name would be. Its
     * rows name each store. Empty on a single-store brief.
     */
    scope: {
      type: String,
      required: false,
      default: '',
    },
    /** One line saying what the figure counts, from the server's reviewed fact list. Empty on older cards. */
    definition: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    ...mapStores(useMainStore),
    all_dealers() {
      return this.mainStore.all_dealers || [];
    },
    /**
     * The heading. The domain when the fact has one, otherwise the fact key with its underscores
     * removed — "open work orders" reads as a heading where "open_work_orders" does not.
     *
     * Falls back to a generic label rather than rendering an empty heading, because the band's layout
     * puts the pill opposite it and a blank left side reads as a rendering fault.
     */
    domain_label() {
      if (this.domain) return this.domain;
      if (this.findingKey) return this.findingKey.replace(/_/g, ' ').trim();
      return this.$t('messaging.briefFinding.untitled');
    },
    /**
     * The pill. It states whether the DATA could be read — not whether anything crossed a line.
     *
     * Briefs have no thresholds, so there is no "triggered" state to show and inventing one would be
     * the single worst thing a card in this thread could do: assert a judgement that nothing computed.
     */
    pill_label() {
      return this.is_unavailable
        ? this.$t('messaging.briefFinding.unavailable')
        : this.$t('messaging.briefFinding.gathered');
    },
    is_unavailable() {
      return String(this.status || '').trim() === 'Unavailable';
    },
    /**
     * Every card shows its pill except the one a brief posts when no card had data. That card is a quiet
     * morning, not a fault, and "Unavailable" beside "nothing to report" would say otherwise.
     */
    show_pill() {
      return String(this.findingKey || '').trim() !== 'brief_empty';
    },
    definition_text() {
      return String(this.definition || '').trim();
    },
    /**
     * Unavailable gets emphasis without alarm, the same treatment an awaiting question gets on a result
     * card: a domain that could not be read is not a fault in the dealership, but it is the one thing on
     * the card the owner has to notice — otherwise a quiet department and a broken tool look identical.
     */
    pill_class() {
      return this.is_unavailable
        ? 'bg-brand-subtle outline outline-1 outline-brand-outline text-text-link'
        : 'bg-surface-3 text-text-subtle';
    },
    time_label() {
      if (!this.producedAt) return '';

      // A DATE is not a time, and must never be rendered as one. Findings delivered before the run
      // instant was stamped carry only the day they cover (yyyy-MM-dd), and a bare date string parses
      // as midnight UTC — so formatting it with {hour, minute} printed the viewer's UTC offset as a
      // clock: every card claimed 7:00 PM in CDT, 6:00 PM in CST, on every finding ever produced.
      // Those findings have no run time and no migration will give them one, so they show none.
      if (/^\d{4}-\d{2}-\d{2}$/.test(this.producedAt.trim())) return '';

      const when = new Date(this.producedAt);
      if (Number.isNaN(when.getTime())) return '';

      try {
        return this.$t('messaging.briefFinding.producedAt', {
          time: when.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' }),
        });
      } catch (error) {
        return '';
      }
    },
    /**
     * The store's name, falling back to the raw CIF. Case-insensitive on the match for the reason the
     * conversation list's own label is: hosts do not agree on CIF casing, and a strict match would cost
     * the card its store name.
     */
    store_label() {
      // A group brief's card speaks for several stores; its anchor store's name here would misname it.
      const scope = String(this.scope || '').trim();
      if (scope) return scope;
      if (!this.dealerId) return '';

      const dealer = this.all_dealers.find(
        candidate => (candidate.cifNumber || '').toLowerCase() === String(this.dealerId).toLowerCase());

      return dealer?.name || dealer?.cifNumber || this.dealerId;
    },
    /**
     * The figures, defensively. This arrives as a parsed metadata value, so a malformed entry is a
     * possibility rather than a contract violation — a row without a label is dropped instead of
     * rendering an empty term beside a number nobody can interpret.
     */
    metric_rows() {
      if (!Array.isArray(this.metrics)) return [];

      return this.metrics
        .filter(row => row && typeof row.label === 'string' && row.label.trim())
        .map(row => ({
          label: row.label.trim(),
          value: row.value === null || row.value === undefined ? '' : String(row.value).trim(),
          // Only a group brief's store rows carry one.
          source: typeof row.source === 'string' ? row.source.trim() : '',
        }));
    },
  },
}
</script>
